<script setup>
/**
 * SzFilterSheet — painel de filtros expansivo que abre abaixo do header
 * da lista de conversas. Substitui o modal nativo de filtros.
 *
 * Constrói um payload no formato `appliedFilter` do Chatwoot:
 *   [{ attributeKey, filterOperator, values, queryOperator }]
 * Emite `apply` (com o payload) e `clear`.
 *
 * `modelValue` controla aberto/fechado (v-model).
 * `initialFilters` (opcional) hidrata o draft ao abrir.
 */
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import SzDatePicker from './SzDatePicker.vue';

// Helpers de cor pra etiquetas
function hexToBg(hex) {
  if (!hex || !hex.startsWith('#')) return '#F1F1F1';
  // Mesma opacidade que CardLabels usa nos cards da lista: hex + '1A'
  // (0x1A / 0xFF ≈ 0.10). Mantém consistência visual entre filtro e cards.
  return `${hex}1A`;
}
function hexToFg(hex) {
  return hex || '#333';
}

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  initialFilters: { type: Array, default: () => [] },
});
const emit = defineEmits(['update:modelValue', 'apply', 'clear']);

const store = useStore();
const router = useRouter();
const route = useRoute();
const agents = useMapGetter('agents/getAgents');
const labels = useMapGetter('labels/getLabels');
// SZ: inboxes (canais) — usado pelo filtro "Canais" multi-select
const inboxes = useMapGetter('inboxes/getInboxes');

// SZ: oculta agentes sem nome válido (placeholders/usuários de sistema)
// que aparecem como "?" no avatar, além de bots de sistema (ex: "iris rede neural").
const EXCLUDED_AGENT_PATTERNS = [/iris\s+rede\s+neural/i];
const validAgents = computed(() =>
  (agents.value || []).filter(a => {
    const name = String(a?.name || '').trim();
    if (name.length === 0 || name === '?') return false;
    if (EXCLUDED_AGENT_PATTERNS.some(re => re.test(name))) return false;
    return true;
  })
);

onMounted(() => {
  if (!agents.value?.length) store.dispatch('agents/get');
  if (!labels.value?.length) store.dispatch('labels/get');
  if (!inboxes.value?.length) store.dispatch('inboxes/get');
});

// ───────── Draft state ─────────
const SORT_OPTIONS = [
  { value: 'last_message_at_desc',  label: 'Última mensagem'  },
  { value: 'last_activity_at_desc', label: 'Última alteração' },
];
const PRIORITY_OPTIONS = [
  { value: 'urgent', label: 'Urgente' },
  { value: 'high',   label: 'Alta'    },
  { value: 'medium', label: 'Média'   },
  { value: 'low',    label: 'Baixa'   },
];
const PERIOD_PRESETS = [
  { key: 'today',     label: 'Hoje'          },
  { key: 'thisMonth', label: 'Este mês'      },
  { key: 'lastMonth', label: 'Mês passado'   },
  { key: 'lastWeek',  label: 'Última semana' },
];

const draftSort = ref('last_message_at_desc');
const draftPriorities = ref(new Set());
const draftAssignees = ref([]);          // [{id, name, thumbnail}]
const draftLabels = ref([]);             // [{id, title, color}]
const draftInboxes = ref([]);            // [{id, name, channel_type}]
const draftUnassignedOnly = ref(false);  // marca "sem responsável"
const draftPeriodPreset = ref(null);
const draftFrom = ref('');               // YYYY-MM-DD
const draftTo = ref('');                 // YYYY-MM-DD

const assigneeDropdownOpen = ref(false);
const labelsDropdownOpen = ref(false);
const inboxesDropdownOpen = ref(false);

// SZ: queries de busca dos dropdowns (Sadraque #237)
const assigneeSearchQuery = ref('');
const inboxesSearchQuery = ref('');
const labelsSearchQuery = ref('');

// ───────── Hidratação dos initialFilters ─────────
function hydrateFromFilters(arr) {
  // reset
  draftPriorities.value = new Set();
  draftAssignees.value = [];
  draftLabels.value = [];
  draftInboxes.value = [];
  draftUnassignedOnly.value = false;
  draftFrom.value = '';
  draftTo.value = '';
  draftPeriodPreset.value = null;

  (arr || []).forEach(f => {
    const key = f.attributeKey || f.attribute_key;
    const op = f.filterOperator || f.filter_operator;
    const vals = f.values || [];
    if (key === 'priority') {
      const arrV = Array.isArray(vals) ? vals : [vals];
      arrV.forEach(v => draftPriorities.value.add(typeof v === 'object' ? v.id : v));
    } else if (key === 'assignee_id') {
      // is_not_present → "Não atribuídas" (sem responsável)
      if (op === 'is_not_present') {
        draftUnassignedOnly.value = true;
      } else {
        const arrV = Array.isArray(vals) ? vals : [vals];
        arrV.forEach(v => {
          const id = typeof v === 'object' ? v.id : v;
          const a = (agents.value || []).find(x => x.id === id);
          if (a) draftAssignees.value.push({ id: a.id, name: a.name, thumbnail: a.thumbnail });
        });
      }
    } else if (key === 'inbox_id') {
      const arrV = Array.isArray(vals) ? vals : [vals];
      arrV.forEach(v => {
        const id = typeof v === 'object' ? v.id : v;
        const ib = (inboxes.value || []).find(x => x.id === id);
        if (ib) draftInboxes.value.push({ id: ib.id, name: ib.name, channel_type: ib.channel_type });
      });
    } else if (key === 'labels') {
      const arrV = Array.isArray(vals) ? vals : [vals];
      arrV.forEach(v => {
        const title = typeof v === 'object' ? v.id : v;
        const l = (labels.value || []).find(x => x.title === title);
        if (l) draftLabels.value.push({ id: l.id, title: l.title, color: l.color });
      });
    } else if (key === 'created_at') {
      const val = Array.isArray(vals) ? vals[0] : vals;
      if (op === 'is_greater_than') draftFrom.value = (val || '').slice(0, 10);
      else if (op === 'is_less_than') draftTo.value = (val || '').slice(0, 10);
    }
  });
  // SZ_FILTER_NAVBAR_PRESELECT_20260526: navbar vence. Se a rota atual tem
  // inbox_id, esse canal é o que deve aparecer pré-selecionado, sobrescrevendo
  // qualquer inbox que tenha vindo de filtros salvos.
  const routeInboxIdLocal = route && route.params && (route.params.inbox_id || route.params.inboxId);
  if (routeInboxIdLocal) {
    const ib = (inboxes.value || []).find(x => String(x.id) === String(routeInboxIdLocal));
    if (ib) {
      draftInboxes.value = [{ id: ib.id, name: ib.name, channel_type: ib.channel_type }];
    }
  }
  // Detecta preset (se ambos batem com algum preset conhecido)
  detectPreset();
}
function detectPreset() {
  if (!draftFrom.value || !draftTo.value) return;
  const today = new Date();
  const pad = n => String(n).padStart(2, '0');
  const iso = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const t = iso(today);
  if (draftFrom.value === t && draftTo.value === t) {
    draftPeriodPreset.value = 'today';
    return;
  }
  // outros presets ficariam aqui — manter conservador
}

watch(() => props.modelValue, isOpen => {
  if (isOpen) hydrateFromFilters(props.initialFilters);
});
watch(() => props.initialFilters, v => {
  if (props.modelValue) hydrateFromFilters(v);
}, { deep: true });
// SZ_FILTER_NAVBAR_PRESELECT_20260526: re-hidrata quando inboxes terminam de
// carregar com a sheet aberta (race entre dispatch inboxes/get e abertura).
watch(() => (inboxes.value || []).length, len => {
  if (props.modelValue && len > 0) hydrateFromFilters(props.initialFilters);
});

// ───────── Helpers ─────────
function togglePriority(v) {
  const s = new Set(draftPriorities.value);
  if (s.has(v)) s.delete(v); else s.add(v);
  draftPriorities.value = s;
}
function priorityActive(v) { return draftPriorities.value.has(v); }

function toggleAssignee(a) {
  // Selecionar agente específico desliga "Não atribuídas" (mutuamente exclusivos).
  if (draftUnassignedOnly.value) draftUnassignedOnly.value = false;
  const exists = draftAssignees.value.find(x => x.id === a.id);
  if (exists) {
    draftAssignees.value = draftAssignees.value.filter(x => x.id !== a.id);
  } else {
    draftAssignees.value = [...draftAssignees.value, { id: a.id, name: a.name, thumbnail: a.thumbnail }];
  }
}
function removeAssignee(id) {
  draftAssignees.value = draftAssignees.value.filter(x => x.id !== id);
}

function toggleLabel(l) {
  const exists = draftLabels.value.find(x => x.id === l.id);
  if (exists) {
    draftLabels.value = draftLabels.value.filter(x => x.id !== l.id);
  } else {
    draftLabels.value = [...draftLabels.value, { id: l.id, title: l.title, color: l.color }];
  }
}
function removeLabel(id) {
  draftLabels.value = draftLabels.value.filter(x => x.id !== id);
}

function setPeriodPreset(key) {
  draftPeriodPreset.value = key;
  const today = new Date();
  const pad = n => String(n).padStart(2, '0');
  const iso = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  if (key === 'today') {
    const t = iso(today);
    draftFrom.value = t;
    draftTo.value = t;
  } else if (key === 'thisMonth') {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    draftFrom.value = iso(start);
    draftTo.value = iso(today);
  } else if (key === 'lastMonth') {
    const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const end = new Date(today.getFullYear(), today.getMonth(), 0);
    draftFrom.value = iso(start);
    draftTo.value = iso(end);
  } else if (key === 'lastWeek') {
    const end = new Date(today);
    end.setDate(today.getDate() - 1);
    const start = new Date(end);
    start.setDate(end.getDate() - 6);
    draftFrom.value = iso(start);
    draftTo.value = iso(end);
  }
}
function onManualDateChange() {
  draftPeriodPreset.value = null;
}

const assigneeLabelDisplay = computed(() => {
  if (!draftAssignees.value.length) return 'Todos';
  return '';
});

function close() { emit('update:modelValue', false); }

function buildPayload() {
  const payload = [];

  // sort_by é tratado separadamente (chatSortFilter), não vai como appliedFilter

  if (draftPriorities.value.size > 0) {
    payload.push({
      attributeKey: 'priority',
      filterOperator: 'equal_to',
      values: Array.from(draftPriorities.value),
      queryOperator: 'and',
      attributeModel: 'standard',
      inputType: 'multi_select',
      dataType: 'text',
      custom_attribute_type: '',
    });
  }
  // SZ: Não atribuídas (assignee_id IS NULL). Coexiste com filtro de responsável
  // específico — quando ambos estão setados, queryOperator AND faz a intersecção
  // (vazio na prática). UI desliga "Não atribuídas" quando o usuário escolhe
  // um agente, e vice-versa.
  if (draftUnassignedOnly.value) {
    payload.push({
      attributeKey: 'assignee_id',
      filterOperator: 'is_not_present',
      values: [],
      queryOperator: 'and',
      attributeModel: 'standard',
      inputType: 'search_select',
      dataType: 'number',
      custom_attribute_type: '',
    });
  } else if (draftAssignees.value.length > 0) {
    payload.push({
      attributeKey: 'assignee_id',
      filterOperator: 'equal_to',
      values: draftAssignees.value.map(a => ({ id: a.id, name: a.name })),
      queryOperator: 'and',
      attributeModel: 'standard',
      inputType: 'search_select',
      dataType: 'number',
      custom_attribute_type: '',
    });
  }
  if (draftInboxes.value.length > 0) {
    payload.push({
      attributeKey: 'inbox_id',
      filterOperator: 'equal_to',
      values: draftInboxes.value.map(i => ({ id: i.id, name: i.name })),
      queryOperator: 'and',
      attributeModel: 'standard',
      inputType: 'multi_select',
      dataType: 'number',
      custom_attribute_type: '',
    });
  }
  if (draftLabels.value.length > 0) {
    // Backend: tag_filter_query usa tags.name IN (:values).
    // filterQueryGenerator extrai .id de objetos; passamos {id: title, name: title}
    // pra garantir que o array final seja [title, title, ...].
    payload.push({
      attributeKey: 'labels',
      filterOperator: 'equal_to',
      values: draftLabels.value.map(l => ({ id: l.title, name: l.title })),
      queryOperator: 'and',
      attributeModel: 'standard',
      inputType: 'multi_select',
      dataType: 'text',
      custom_attribute_type: '',
    });
  }
  if (draftFrom.value) {
    payload.push({
      attributeKey: 'created_at',
      filterOperator: 'is_greater_than',
      values: [draftFrom.value],
      queryOperator: 'and',
      attributeModel: 'standard',
      inputType: 'date',
      dataType: 'date',
      custom_attribute_type: '',
    });
  }
  if (draftTo.value) {
    payload.push({
      attributeKey: 'created_at',
      filterOperator: 'is_less_than',
      values: [draftTo.value],
      queryOperator: 'and',
      attributeModel: 'standard',
      inputType: 'date',
      dataType: 'date',
      custom_attribute_type: '',
    });
  }
  return payload;
}

async function applyFilters() {
  var payload = { sortBy: draftSort.value, filters: buildPayload() };

  // SZ_FILTER_NAVBAR_SYNC_20260526: filtro de canais e navbar (rota) precisam
  // ficar coerentes. Regras:
  //   - 1 canal no filtro → navega pra rota inbox_dashboard desse canal
  //     (navbar acompanha). URL cobre o canal, então removemos inbox_id do
  //     payload dispatched.
  //   - 0 canais no filtro + rota com inbox → home (limpa o scope da navbar).
  //   - >1 canais + rota com inbox → home (multi-select não cabe na URL,
  //     ChatList aplica os filtros dispatched que carregam o multi).
  //   - 1 canal igual ao da rota / 0 canais + rota sem inbox → sem navegação.
  //
  // Ordem necessária quando há navegação:
  //   1) close() — fecha a sheet (UX) e evita reapply duplo
  //   2) router.push + nextTick — deixa o watcher de inbox da ChatList disparar
  //      e o clearConversationFilters implícito assentar
  //   3) dispatch dos filtros — ChatList lê em re-mount
  var wantedInboxIds = (draftInboxes.value || []).map(i => Number(i.id));
  var routeInboxId = route && route.params && (route.params.inbox_id || route.params.inboxId);
  var routeInboxIdNum = routeInboxId ? Number(routeInboxId) : null;
  var accountId = route.params.accountId;

  var targetRoute = null;
  if (wantedInboxIds.length === 1 && wantedInboxIds[0] !== routeInboxIdNum) {
    targetRoute = { name: 'inbox_dashboard', params: { accountId: accountId, inbox_id: String(wantedInboxIds[0]) } };
  } else if ((wantedInboxIds.length === 0 || wantedInboxIds.length > 1) && routeInboxIdNum) {
    targetRoute = { name: 'home', params: { accountId: accountId } };
  }

  // Quando o destino é uma rota de inbox único, retiramos inbox_id do payload
  // (URL já filtra). Nos outros casos, mantemos.
  var goingToSingleInboxRoute = wantedInboxIds.length === 1;
  var filtersDispatch = goingToSingleInboxRoute
    ? (payload.filters || []).filter(f => (f.attributeKey || f.attribute_key) !== 'inbox_id')
    : (payload.filters || []);

  if (targetRoute) {
    close();
    try { await router.push(targetRoute); }
    catch (e) { /* duplicated nav — ignore */ }
    await nextTick();
    if (payload.sortBy) {
      store.dispatch('setChatSortFilter', payload.sortBy);
    }
    if (filtersDispatch.length > 0) {
      store.dispatch(
        'setConversationFilters',
        useSnakeCase(JSON.parse(JSON.stringify(filtersDispatch)))
      );
    } else {
      store.dispatch('clearConversationFilters');
    }
    return;
  }

  // Sem navegação: emit normal. Se estamos na rota de inbox e o usuário manteve
  // só esse mesmo canal no filtro, dispatchamos sem o inbox_id pra não duplicar.
  emit('apply', { sortBy: draftSort.value, filters: filtersDispatch });
  close();
}

function clearFilters() {
  draftSort.value = 'last_message_at_desc';
  draftPriorities.value = new Set();
  draftAssignees.value = [];
  draftLabels.value = [];
  draftInboxes.value = [];
  draftUnassignedOnly.value = false;
  draftFrom.value = '';
  draftTo.value = '';
  draftPeriodPreset.value = null;
  emit('clear');
  close();
}

// ───────── Toggles dos novos filtros (Inboxes / Não atribuídas) ─────────
function toggleInbox(ib) {
  const idx = draftInboxes.value.findIndex(x => x.id === ib.id);
  if (idx >= 0) draftInboxes.value.splice(idx, 1);
  else draftInboxes.value.push({ id: ib.id, name: ib.name, channel_type: ib.channel_type });
}
function removeInbox(id) {
  draftInboxes.value = draftInboxes.value.filter(x => x.id !== id);
}
function toggleUnassigned() {
  draftUnassignedOnly.value = !draftUnassignedOnly.value;
  // Não atribuídas é mutuamente exclusivo com responsável específico (no SQL
  // backend o AND `assignee IS NULL AND assignee = N` daria vazio sempre).
  if (draftUnassignedOnly.value) draftAssignees.value = [];
}

// ───────── Busca + "Selecionar todas" (Sadraque #237) ─────────
const filteredAgents = computed(() => {
  const q = assigneeSearchQuery.value.trim().toLowerCase();
  const base = validAgents.value || [];
  if (!q) return base;
  return base.filter(a => String(a?.name || '').toLowerCase().includes(q));
});
const filteredInboxes = computed(() => {
  const q = inboxesSearchQuery.value.trim().toLowerCase();
  const base = inboxes.value || [];
  if (!q) return base;
  return base.filter(i => String(i?.name || '').toLowerCase().includes(q));
});
// SZ: labels search — mirrors filteredAgents/filteredInboxes. Filters the
// labels list against the search query (case-insensitive, by title).
const filteredLabels = computed(() => {
  const q = labelsSearchQuery.value.trim().toLowerCase();
  const base = labels.value || [];
  if (!q) return base;
  return base.filter(l => String(l?.title || '').toLowerCase().includes(q));
});
// "Selecionar todas" pra Responsáveis: liga se TODOS os filteredAgents estão em draft.
const allAgentsSelected = computed(() => {
  const list = filteredAgents.value;
  if (!list.length) return false;
  return list.every(a => draftAssignees.value.some(x => x.id === a.id));
});
function toggleAllAgents() {
  if (allAgentsSelected.value) {
    // deselecionar somente os atualmente filtrados
    const ids = new Set(filteredAgents.value.map(a => a.id));
    draftAssignees.value = draftAssignees.value.filter(x => !ids.has(x.id));
  } else {
    // adicionar todos os filtrados que ainda não estão
    if (draftUnassignedOnly.value) draftUnassignedOnly.value = false;
    const next = [...draftAssignees.value];
    filteredAgents.value.forEach(a => {
      if (!next.some(x => x.id === a.id)) {
        next.push({ id: a.id, name: a.name, thumbnail: a.thumbnail });
      }
    });
    draftAssignees.value = next;
  }
}
const allInboxesSelected = computed(() => {
  const list = filteredInboxes.value;
  if (!list.length) return false;
  return list.every(i => draftInboxes.value.some(x => x.id === i.id));
});
function toggleAllInboxes() {
  if (allInboxesSelected.value) {
    const ids = new Set(filteredInboxes.value.map(i => i.id));
    draftInboxes.value = draftInboxes.value.filter(x => !ids.has(x.id));
  } else {
    const next = [...draftInboxes.value];
    filteredInboxes.value.forEach(i => {
      if (!next.some(x => x.id === i.id)) {
        next.push({ id: i.id, name: i.name, channel_type: i.channel_type });
      }
    });
    draftInboxes.value = next;
  }
}

// Resolve um ícone SVG inline pra cada tipo de canal (whatsapp, fb messenger,
// instagram, api/web, email, sms, telegram, line, twilio). Caso não conheça,
// devolve um ícone genérico de "code/integration".
function channelIconSvg(channelType) {
  const t = String(channelType || '').toLowerCase();
  // Wrappers: `outline` (stroke) pra ícones genéricos, `solid` (fill) pra logos de marca
  const outline = (path) => `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">${path}</svg>`;
  const solid = (path) => `<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">${path}</svg>`;
  if (t.includes('whatsapp')) {
    // Logo oficial do WhatsApp — path real do brand
    return solid('<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>');
  }
  if (t.includes('messenger') || t.includes('facebook')) return outline('<path d="M12 2C6.5 2 2 6 2 11c0 2.8 1.4 5.3 3.7 7v3.8l3.4-1.9c1 .3 1.9.4 2.9.4 5.5 0 10-4 10-9S17.5 2 12 2z"/><path d="M6.5 13.5l3-3.5L12 11l3-3.5 2.5 3.5"/>');
  if (t.includes('instagram')) return outline('<rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="0.6" fill="currentColor"/>');
  if (t.includes('telegram')) return outline('<path d="M22 3 2 11l5 2 2 7 4-4 6 5z"/>');
  if (t.includes('email')) return outline('<rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/>');
  if (t.includes('sms') || t.includes('twilio')) return outline('<path d="M21 12a7 7 0 0 1-7 7H8l-4 3v-7a7 7 0 0 1 7-7h3a7 7 0 0 1 7 4z"/>');
  if (t.includes('line')) return outline('<rect x="3" y="3" width="18" height="18" rx="5"/><path d="M7 13v-3M11 13v-3M11 10l2 3v-3M16 10v3"/>');
  if (t.includes('api') || t.includes('web')) return outline('<polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>');
  return outline('<polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>');
}

// Abre um dropdown e fecha os outros (mutex entre os 3 fields).
function toggleDropdown(name) {
  const states = {
    assignee: assigneeDropdownOpen,
    labels: labelsDropdownOpen,
    inboxes: inboxesDropdownOpen,
  };
  const target = states[name];
  if (!target) return;
  const wasOpen = target.value;
  // Fecha todos
  assigneeDropdownOpen.value = false;
  labelsDropdownOpen.value = false;
  inboxesDropdownOpen.value = false;
  // Se estava fechado, abre só o pedido (se estava aberto, fica fechado)
  if (!wasOpen) target.value = true;
}

// fechar dropdowns ao clicar fora
function onClickOutsideDropdowns(e) {
  const t = e.target;
  if (!t.closest('.sz-fs-multi-wrap')) {
    assigneeDropdownOpen.value = false;
    labelsDropdownOpen.value = false;
    inboxesDropdownOpen.value = false;
  }
}
onMounted(() => {
  document.addEventListener('mousedown', onClickOutsideDropdowns);
});
onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onClickOutsideDropdowns);
});
</script>

<template>
  <Transition name="sz-fs">
    <div v-if="modelValue" class="sz-fs" aria-label="Filtros avançados">
      <!-- Header -->
      <div class="sz-fs-header">
        <div
          class="sz-fs-close"
          role="button"
          tabindex="0"
          aria-label="Fechar"
          @click="close"
          @keydown.enter.prevent="close"
          @keydown.space.prevent="close"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </div>
        <span class="sz-fs-title">Filtro</span>
        <div class="sz-fs-header-spacer" />
        <div
          class="sz-fs-funnel"
          role="button"
          tabindex="0"
          aria-label="Fechar filtros"
          @click="close"
          @keydown.enter.prevent="close"
          @keydown.space.prevent="close"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15.625 10.625C15.625 10.7908 15.5592 10.9497 15.4419 11.0669C15.3247 11.1842 15.1658 11.25 15 11.25H5C4.83424 11.25 4.67527 11.1842 4.55806 11.0669C4.44085 10.9497 4.375 10.7908 4.375 10.625C4.375 10.4592 4.44085 10.3003 4.55806 10.1831C4.67527 10.0658 4.83424 10 5 10H15C15.1658 10 15.3247 10.0658 15.4419 10.1831C15.5592 10.3003 15.625 10.4592 15.625 10.625ZM18.125 6.25H1.875C1.70924 6.25 1.55027 6.31585 1.43306 6.43306C1.31585 6.55027 1.25 6.70924 1.25 6.875C1.25 7.04076 1.31585 7.19973 1.43306 7.31694C1.55027 7.43415 1.70924 7.5 1.875 7.5H18.125C18.2908 7.5 18.4497 7.43415 18.5669 7.31694C18.6842 7.19973 18.75 7.04076 18.75 6.875C18.75 6.70924 18.6842 6.55027 18.5669 6.43306C18.4497 6.31585 18.2908 6.25 18.125 6.25ZM11.875 13.75H8.125C7.95924 13.75 7.80027 13.8158 7.68306 13.9331C7.56585 14.0503 7.5 14.2092 7.5 14.375C7.5 14.5408 7.56585 14.6997 7.68306 14.8169C7.80027 14.9342 7.95924 15 8.125 15H11.875C12.0408 15 12.1997 14.9342 12.3169 14.8169C12.4342 14.6997 12.5 14.5408 12.5 14.375C12.5 14.2092 12.4342 14.0503 12.3169 13.9331C12.1997 13.8158 12.0408 13.75 11.875 13.75Z" fill="currentColor"/>
          </svg>
        </div>
      </div>

      <!-- Body -->
      <div class="sz-fs-body">
        <!-- Ordenar -->
        <section class="sz-fs-section">
          <label class="sz-fs-section-label">Filtrar por</label>
          <div class="sz-fs-segmented">
            <div
              v-for="o in SORT_OPTIONS"
              :key="o.value"
              role="tab"
              tabindex="0"
              class="sz-fs-segmented-item"
              :class="{ 'sz-fs-segmented-item--on': draftSort === o.value }"
              @click="draftSort = o.value"
              @keydown.enter.prevent="draftSort = o.value"
              @keydown.space.prevent="draftSort = o.value"
            ><span>{{ o.label }}</span></div>
          </div>
        </section>

        <!-- Prioridade -->
        <section class="sz-fs-section">
          <label class="sz-fs-section-label">Prioridade</label>
          <div class="sz-fs-prio-row">
            <div
              v-for="p in PRIORITY_OPTIONS"
              :key="p.value"
              role="checkbox"
              tabindex="0"
              :aria-checked="priorityActive(p.value)"
              class="sz-fs-prio"
              :class="{ 'sz-fs-prio--on': priorityActive(p.value) }"
              @click="togglePriority(p.value)"
              @keydown.enter.prevent="togglePriority(p.value)"
              @keydown.space.prevent="togglePriority(p.value)"
            >
              <span class="sz-fs-prio-label">{{ p.label }}</span>
              <span class="sz-fs-prio-icon">
                <svg v-if="priorityActive(p.value)" width="16" height="16" viewBox="0 0 24 24" fill="#236D23" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-1.5 14.5l-4-4 1.4-1.4 2.6 2.6 5.6-5.6 1.4 1.4-7 7z"/>
                </svg>
                <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#A1A1A1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              </span>
            </div>
          </div>
        </section>

        <!-- Responsável -->
        <section class="sz-fs-section">
          <label class="sz-fs-section-label">Responsável</label>
          <div class="sz-fs-multi-wrap">
            <div
              class="sz-fs-multi-field"
              @click="toggleDropdown('assignee')"
            >
              <div class="sz-fs-multi-tags">
                <template v-if="!draftUnassignedOnly && draftAssignees.length === 0">
                  <span class="sz-fs-tag-agent sz-fs-tag-agent--noavatar" @click.stop>
                    <span class="sz-fs-tag-text">Todos</span>
                  </span>
                </template>
                <template v-else>
                  <span
                    v-if="draftUnassignedOnly"
                    class="sz-fs-tag-agent sz-fs-tag-agent--noavatar"
                    @click.stop
                  >
                    <!-- Sem avatar — design Sadraque #237 -->
                    <span class="sz-fs-tag-text">Não atribuídas</span>
                    <span class="sz-fs-tag-x" @click.stop="draftUnassignedOnly = false">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </span>
                  </span>
                  <span
                    v-for="a in draftAssignees.slice(0, 3)"
                    :key="a.id"
                    class="sz-fs-tag-agent"
                    @click.stop
                  >
                    <img v-if="a.thumbnail" :src="a.thumbnail" :alt="a.name" class="sz-fs-tag-avatar" />
                    <span v-else class="sz-fs-tag-avatar sz-fs-tag-avatar--initial">{{ (a.name || '?')[0] }}</span>
                    <span class="sz-fs-tag-text">{{ a.name }}</span>
                    <span class="sz-fs-tag-x" @click.stop="removeAssignee(a.id)">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </span>
                  </span>
                  <span v-if="draftAssignees.length > 3" class="sz-fs-tag-overflow">
                    +{{ draftAssignees.length - 3 }}
                  </span>
                </template>
              </div>
              <svg class="sz-fs-multi-chevron" :class="{ open: assigneeDropdownOpen }" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="6 9 12 15 18 9"/></svg>
            </div>
            <div v-if="assigneeDropdownOpen" class="sz-fs-multi-dropdown sz-fs-multi-dropdown--search" @click.stop>
              <!-- Search (Sadraque #237) -->
              <div class="sz-fs-dd-search">
                <svg class="sz-fs-dd-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input
                  v-model="assigneeSearchQuery"
                  type="text"
                  class="sz-fs-dd-search-input"
                  placeholder="Pesquisar responsável"
                />
              </div>
              <div class="sz-fs-dd-scroll">
                <!-- Selecionar todas -->
                <div class="sz-fs-multi-option" @click="toggleAllAgents">
                  <span class="sz-fs-checkbox" :class="{ 'sz-fs-checkbox--on': allAgentsSelected }">
                    <svg v-if="allAgentsSelected" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </span>
                  <span class="sz-fs-option-text sz-fs-option-text--bold">Selecionar todas</span>
                </div>
                <!-- Não atribuídas — sem avatar (design Sadraque #237) -->
                <div
                  class="sz-fs-multi-option"
                  @click="toggleUnassigned"
                >
                  <span class="sz-fs-checkbox" :class="{ 'sz-fs-checkbox--on': draftUnassignedOnly }">
                    <svg v-if="draftUnassignedOnly" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </span>
                  <span class="sz-fs-option-text">Não atribuídas</span>
                </div>
                <!-- Agentes (filtrados pela busca) -->
                <div
                  v-for="a in filteredAgents"
                  :key="a.id"
                  class="sz-fs-multi-option"
                  @click="toggleAssignee(a)"
                >
                  <span class="sz-fs-checkbox" :class="{ 'sz-fs-checkbox--on': !!draftAssignees.find(x => x.id === a.id) }">
                    <svg v-if="draftAssignees.find(x => x.id === a.id)" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </span>
                  <img v-if="a.thumbnail" :src="a.thumbnail" :alt="a.name" class="sz-fs-option-avatar" />
                  <span v-else class="sz-fs-option-avatar sz-fs-option-avatar--initial">{{ (a.name || '?')[0] }}</span>
                  <span class="sz-fs-option-text">{{ a.name }}</span>
                </div>
                <div v-if="!filteredAgents.length" class="sz-fs-multi-empty">Nenhum responsável</div>
              </div>
              <!-- Gradient mask absoluto na borda inferior do dropdown -->
              <div class="sz-fs-dd-gradient" />
            </div>
          </div>
        </section>

        <!-- Etiquetas -->
        <section class="sz-fs-section">
          <label class="sz-fs-section-label">Etiquetas</label>
          <div class="sz-fs-multi-wrap">
            <div
              class="sz-fs-multi-field"
              @click="toggleDropdown('labels')"
            >
              <div class="sz-fs-multi-tags">
                <template v-if="draftLabels.length === 0">
                  <span class="sz-fs-placeholder">Selecione as etiquetas</span>
                </template>
                <template v-else>
                  <span
                    v-for="l in draftLabels.slice(0, 3)"
                    :key="l.id"
                    class="sz-fs-tag-label"
                    :style="{
                      background: hexToBg(l.color),
                      color: hexToFg(l.color),
                    }"
                    @click.stop
                  >
                    <span class="sz-fs-tag-dot" :style="{ background: l.color || '#666' }" />
                    <span class="sz-fs-tag-text">{{ l.title }}</span>
                    <span class="sz-fs-tag-x" @click.stop="removeLabel(l.id)">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </span>
                  </span>
                  <span v-if="draftLabels.length > 3" class="sz-fs-tag-overflow">
                    +{{ draftLabels.length - 3 }}
                  </span>
                </template>
              </div>
              <svg class="sz-fs-multi-chevron" :class="{ open: labelsDropdownOpen }" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="6 9 12 15 18 9"/></svg>
            </div>
            <div v-if="labelsDropdownOpen" class="sz-fs-multi-dropdown sz-fs-multi-dropdown--search" @click.stop>
              <!-- Search (Sadraque #237 parity) -->
              <div class="sz-fs-dd-search">
                <svg class="sz-fs-dd-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input
                  v-model="labelsSearchQuery"
                  type="text"
                  class="sz-fs-dd-search-input"
                  placeholder="Pesquisar etiquetas"
                />
              </div>
              <div class="sz-fs-dd-scroll">
                <div
                  v-for="l in filteredLabels"
                  :key="l.id"
                  class="sz-fs-multi-option"
                  :class="{ 'sz-fs-multi-option--on': !!draftLabels.find(x => x.id === l.id) }"
                  @click="toggleLabel(l)"
                >
                  <span class="sz-fs-option-dot" :style="{ background: l.color || '#666' }" />
                  <span class="sz-fs-option-text">{{ l.title }}</span>
                  <svg v-if="draftLabels.find(x => x.id === l.id)" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#388E3C" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <div v-if="!filteredLabels.length" class="sz-fs-multi-empty">Sem etiquetas</div>
              </div>
              <div class="sz-fs-dd-gradient" />
            </div>
          </div>
        </section>

        <!-- Canais (inboxes) — multi-select -->
        <section class="sz-fs-section">
          <label class="sz-fs-section-label">Canais</label>
          <div class="sz-fs-multi-wrap">
            <div
              class="sz-fs-multi-field"
              @click="toggleDropdown('inboxes')"
            >
              <div class="sz-fs-multi-tags">
                <template v-if="draftInboxes.length === 0">
                  <span class="sz-fs-placeholder">Todos os canais</span>
                </template>
                <template v-else>
                  <span
                    v-for="i in draftInboxes.slice(0, 3)"
                    :key="i.id"
                    class="sz-fs-tag-agent"
                    @click.stop
                  >
                    <span class="sz-fs-tag-avatar sz-fs-tag-avatar--initial">{{ (i.name || '?')[0] }}</span>
                    <span class="sz-fs-tag-text">{{ i.name }}</span>
                    <span class="sz-fs-tag-x" @click.stop="removeInbox(i.id)">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </span>
                  </span>
                  <span v-if="draftInboxes.length > 3" class="sz-fs-tag-overflow">
                    +{{ draftInboxes.length - 3 }}
                  </span>
                </template>
              </div>
              <svg class="sz-fs-multi-chevron" :class="{ open: inboxesDropdownOpen }" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="6 9 12 15 18 9"/></svg>
            </div>
            <div v-if="inboxesDropdownOpen" class="sz-fs-multi-dropdown sz-fs-multi-dropdown--search" @click.stop>
              <!-- Search -->
              <div class="sz-fs-dd-search">
                <svg class="sz-fs-dd-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input
                  v-model="inboxesSearchQuery"
                  type="text"
                  class="sz-fs-dd-search-input"
                  placeholder="Pesquisar canais"
                />
              </div>
              <div class="sz-fs-dd-scroll">
                <!-- Selecionar todas -->
                <div class="sz-fs-multi-option" @click="toggleAllInboxes">
                  <span class="sz-fs-checkbox" :class="{ 'sz-fs-checkbox--on': allInboxesSelected }">
                    <svg v-if="allInboxesSelected" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </span>
                  <span class="sz-fs-option-text sz-fs-option-text--bold">Selecionar todas</span>
                </div>
                <!-- Canais (filtrados) -->
                <div
                  v-for="i in filteredInboxes"
                  :key="i.id"
                  class="sz-fs-multi-option"
                  @click="toggleInbox(i)"
                >
                  <span class="sz-fs-checkbox" :class="{ 'sz-fs-checkbox--on': !!draftInboxes.find(x => x.id === i.id) }">
                    <svg v-if="draftInboxes.find(x => x.id === i.id)" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </span>
                  <span class="sz-fs-channel-icon" v-html="channelIconSvg(i.channel_type)" />
                  <span class="sz-fs-option-text">{{ i.name }}</span>
                </div>
                <div v-if="!filteredInboxes.length" class="sz-fs-multi-empty">Sem canais</div>
              </div>
              <!-- Gradient mask absoluto na borda inferior do dropdown -->
              <div class="sz-fs-dd-gradient" />
            </div>
          </div>
        </section>

        <!-- Período -->
        <section class="sz-fs-section">
          <label class="sz-fs-section-label">Período</label>
          <div class="sz-fs-period-presets">
            <div
              v-for="p in PERIOD_PRESETS"
              :key="p.key"
              class="sz-fs-preset"
              :class="{ 'sz-fs-preset--on': draftPeriodPreset === p.key }"
              role="button"
              tabindex="0"
              @click="setPeriodPreset(p.key)"
              @keydown.enter.prevent="setPeriodPreset(p.key)"
              @keydown.space.prevent="setPeriodPreset(p.key)"
            >{{ p.label }}</div>
          </div>
          <div class="sz-fs-date-row">
            <div class="sz-fs-date-field">
              <span class="sz-fs-date-prefix">Início:</span>
              <SzDatePicker
                v-model="draftFrom"
                :max="draftTo || ''"
                placeholder="dd/mm/aaaa"
                @update:modelValue="onManualDateChange"
              />
            </div>
            <div class="sz-fs-date-field">
              <span class="sz-fs-date-prefix">Fim:</span>
              <SzDatePicker
                v-model="draftTo"
                :min="draftFrom || ''"
                placeholder="dd/mm/aaaa"
                @update:modelValue="onManualDateChange"
              />
            </div>
          </div>
        </section>

        <!-- Footer -->
        <div class="sz-fs-footer">
          <button type="button" class="sz-fs-btn sz-fs-btn--ghost" @click="clearFilters">Limpar filtros</button>
          <button type="button" class="sz-fs-btn sz-fs-btn--primary" @click="applyFilters">Filtrar</button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
/* Container — absolute cobrindo header + chips (top:0). z-index abaixo da pill de colapsar sidebar (z>=20 do Chatwoot) */
.sz-fs {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  z-index: 10;
  background: #FFFFFF;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  box-shadow: 0px 4px 10px rgba(15, 23, 42, 0.1);
  font-family: 'Inter', system-ui, sans-serif;
  overflow: hidden;
}

/* Header da sheet */
.sz-fs-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  height: 56px;
  border-bottom: 1px solid #E5E7EB;
  background: #FFFFFF;
  box-sizing: border-box;
}
/* X usa <div>+role="button" pra escapar do reset global de button do Chatwoot */
.sz-fs-close {
  width: 20px; height: 20px;
  display: flex; align-items: center; justify-content: center;
  color: #000000; cursor: pointer;
  background: transparent;
  user-select: none;
}
.sz-fs-close:hover { opacity: 0.7; }
.sz-fs-title {
  font-size: 16px; font-weight: 600; line-height: 24px;
  color: #000000;
}
.sz-fs-header-spacer { flex: 1; }
/* Funnel usa <div> + bg #F0F0F0 (estilo Figma) */
.sz-fs-funnel {
  width: 32px; height: 32px;
  background: #F0F0F0;
  border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  color: #000000; cursor: pointer;
  user-select: none;
}
.sz-fs-funnel:hover { background: #E5E5E5; }

/* Body */
.sz-fs-body {
  display: flex;
  flex-direction: column;
  padding: 12px;
  gap: 16px;
}
.sz-fs-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.sz-fs-section-label {
  font-size: 14px; font-weight: 500; line-height: 20px;
  color: #262626;
}

/* Segmented "Filtrar por" */
.sz-fs-segmented {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 4px;
  background: #F1F1F1;
  border-radius: 8px;
  box-sizing: border-box;
  align-self: flex-start;
}
.sz-fs-segmented-item {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  padding: 0 12px;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s;
}
.sz-fs-segmented-item span {
  font-size: 14px; font-weight: 500; color: #5C5C5C;
}
.sz-fs-segmented-item--on {
  background: #FFFFFF;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}
.sz-fs-segmented-item--on span { color: #3F3F3F; }

/* Prioridade chips */
.sz-fs-prio-row { display: flex; flex-wrap: wrap; gap: 4px; }
.sz-fs-prio {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  padding: 0 4px 0 8px;
  background: #FFFFFF;
  border: 1px solid #E5E5E5;
  border-radius: 999px;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s, border-color 0.15s;
}
.sz-fs-prio:hover { background: rgba(56,142,60,0.04); }
.sz-fs-prio--on {
  background: #DFEFE3;
  border-color: transparent;
}
.sz-fs-prio--on:hover { background: #D2E7D6; }
.sz-fs-prio-label {
  font-size: 12px; font-weight: 500; line-height: 20px; color: #000000;
}
.sz-fs-prio-icon { display: inline-flex; align-items: center; justify-content: center; width: 16px; height: 16px; }

/* Multi-select wrapper (Responsável / Etiquetas) */
.sz-fs-multi-wrap { position: relative; }
.sz-fs-multi-field {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 32px;
  /* Cap altura pra evitar push do conteúdo abaixo quando há muitas tags. */
  max-height: 80px;
  padding: 4px 8px;
  background: #FFFFFF;
  border: 1px solid #D1D5DB;
  border-radius: 8px;
  cursor: pointer;
  box-sizing: border-box;
  overflow: hidden;
}
.sz-fs-multi-tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
  max-height: 72px;
  overflow-y: auto;
}
/* Scrollbar discreto pra lista de tags quando overflow */
.sz-fs-multi-tags::-webkit-scrollbar { width: 4px; }
.sz-fs-multi-tags::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.18); border-radius: 4px; }
.dark .sz-fs-multi-tags::-webkit-scrollbar-thumb { background: #3E3E43; }
.sz-fs-placeholder { font-size: 14px; color: #6B7280; }
.sz-fs-multi-chevron { color: #6B7280; flex-shrink: 0; transition: transform 0.15s; }
.sz-fs-multi-chevron.open { transform: rotate(180deg); }

/* Tag de agente */
.sz-fs-tag-agent {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 20px;
  padding: 0 4px;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 4px;
  font-size: 12px;
}
.sz-fs-tag-avatar {
  width: 16px; height: 16px; border-radius: 999px; object-fit: cover;
}
.sz-fs-tag-avatar--initial {
  display: inline-flex; align-items: center; justify-content: center;
  background: #FF5500; color: #FFFFFF; font-size: 9px; font-weight: 600;
}
.sz-fs-tag-text {
  font-family: inherit;
  font-size: 12px; font-weight: 400; color: #000000;
  max-width: 100px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
/* X — caixinha cinza com ícone discreto (Sadraque #237) */
.sz-fs-tag-x {
  display: inline-flex; align-items: center; justify-content: center;
  width: 16px; height: 16px;
  padding: 2px;
  background: #E5E5E5;
  border-radius: 4px;
  cursor: pointer;
  color: #6B6B6B;
  flex-shrink: 0;
}
.sz-fs-tag-x:hover { background: #D4D4D8; color: #111; }
.sz-fs-tag-x svg { width: 12px; height: 12px; }

/* Tag de label — alinhada com .sz-clabel de CardLabels.vue (cards da lista) */
.sz-fs-tag-label {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 4px 2px 4px;
  height: 16px;
  border-radius: 29px;
  font-size: 11px;
  font-weight: 500;
  line-height: 100%;
}
.sz-fs-tag-label .sz-fs-tag-text {
  font-size: 11px;
  font-weight: 500;
  color: inherit;
  max-width: 120px;
}
.sz-fs-tag-label .sz-fs-tag-x {
  width: 14px;
  height: 14px;
  background: transparent;
  color: inherit;
  opacity: 0.7;
  border-radius: 999px;
}
.sz-fs-tag-label .sz-fs-tag-x:hover {
  background: rgba(0, 0, 0, 0.08);
  opacity: 1;
}
.sz-fs-tag-label .sz-fs-tag-x svg {
  width: 10px;
  height: 10px;
}
.sz-fs-tag-dot {
  width: 9px; height: 9px; min-width: 9px;
  border-radius: 999px;
  flex-shrink: 0;
}
.sz-fs-tag-overflow {
  display: inline-flex; align-items: center; justify-content: center;
  height: 16px; padding: 0 6px;
  background: #E1EBDC; color: #388E3C;
  border-radius: 24px;
  font-size: 12px; font-weight: 600;
}

/* Dropdown */
.sz-fs-multi-dropdown {
  position: absolute;
  left: 0; right: 0; top: calc(100% + 4px);
  z-index: 60;
  max-height: 220px;
  overflow-y: auto;
  background: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.12);
  padding: 4px;
}
.sz-fs-multi-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
}
.sz-fs-multi-option:hover { background: rgba(56,142,60,0.06); }
.sz-fs-multi-option--on { background: rgba(56,142,60,0.10); }
.sz-fs-option-avatar {
  width: 24px; height: 24px; border-radius: 999px; object-fit: cover; flex-shrink: 0;
}
.sz-fs-option-avatar--initial {
  display: inline-flex; align-items: center; justify-content: center;
  background: #FF5500; color: #FFFFFF; font-size: 11px; font-weight: 600;
}
.sz-fs-option-dot { width: 10px; height: 10px; border-radius: 999px; flex-shrink: 0; }
.sz-fs-option-text {
  flex: 1; min-width: 0;
  font-size: 13px; color: #000000;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.sz-fs-multi-empty { padding: 8px; font-size: 12px; color: #6B7280; text-align: center; }

/* SZ: dropdown com search (Sadraque #237) — Responsável + Canais
   Estrutura: [search fixo][scroll com items][gradient absoluto na borda] */
.sz-fs-multi-dropdown--search {
  padding: 0;
  max-height: 260px;
  position: absolute; /* SZ: era relative — entrava no fluxo e empurrava os campos abaixo. absolute mantem o dropdown flutuando (herda left/right/top/z-index da .sz-fs-multi-dropdown). */
  overflow: hidden;
}
/* SZ: search pill (Figma — "Pesquisar canais")
   Flex row, lupa + input centrados verticalmente. !important blindando
   contra resets globais de <input> do Chatwoot (padding/border/bg). */
.sz-fs-dd-search {
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  gap: 10px !important;
  margin: 8px 12px !important;
  padding: 8px 12px !important;
  background: #FFFFFF !important;
  border: 1px solid #E5E5E5 !important;
  border-radius: 8px !important;
  box-sizing: border-box !important;
  min-height: 36px !important;
  width: auto !important;
}
.sz-fs-dd-search-icon {
  flex: 0 0 16px !important;
  display: block !important;
  width: 16px !important;
  height: 16px !important;
  color: #9CA3AF !important;
}
.sz-fs-dd-search-input {
  flex: 1 1 auto !important;
  min-width: 0 !important;
  width: 100% !important;
  height: 20px !important;
  line-height: 20px !important;
  padding: 0 !important;
  margin: 0 !important;
  background: transparent !important;
  border: 0 !important;
  outline: 0 !important;
  font-family: 'Inter', system-ui, sans-serif !important;
  font-weight: 500 !important;
  font-size: 14px !important;
  color: #111 !important;
  box-sizing: border-box !important;
  box-shadow: none !important;
  -webkit-appearance: none !important;
  appearance: none !important;
}
.sz-fs-dd-search-input:focus { outline: 0 !important; box-shadow: none !important; border: 0 !important; }
.sz-fs-dd-search-input::placeholder { color: #9CA3AF !important; opacity: 1 !important; }
/* Wrapper de scroll — lista de opções */
.sz-fs-dd-scroll {
  max-height: 208px;
  overflow-y: auto;
  padding: 4px 4px 8px 4px;
}
.sz-fs-dd-scroll::-webkit-scrollbar { width: 8px; }
.sz-fs-dd-scroll::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.18); border-radius: 4px; }
.sz-fs-dd-scroll::-webkit-scrollbar-track { background: transparent; }
.dark .sz-fs-dd-scroll::-webkit-scrollbar-thumb { background: #3E3E43; }
/* Gradient mask absoluto, grudado na borda inferior do dropdown.
   pointer-events:none pra não capturar cliques no último item. */
.sz-fs-dd-gradient {
  position: absolute;
  left: 1px;
  right: 1px;
  bottom: 0;
  height: 36px;
  pointer-events: none;
  background: linear-gradient(180deg, rgba(255,255,255,0) 0%, #FFFFFF 100%);
  border-radius: 0 0 8px 8px;
}
/* Checkbox quadrado (substitui o check icon do lado direito) */
.sz-fs-checkbox {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px; height: 16px;
  background: #FFFFFF;
  border: 1.5px solid #D4D4D8;
  border-radius: 4px;
  flex-shrink: 0;
  color: transparent;
  transition: background 0.12s, border-color 0.12s;
}
.sz-fs-checkbox--on {
  background: #59AA59;
  border-color: #59AA59;
  color: #FFFFFF;
}
.sz-fs-option-text--bold { font-weight: 600; }
.sz-fs-channel-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #9797A0;
  flex-shrink: 0;
}

/* Período */
.sz-fs-period-presets { display: flex; flex-wrap: wrap; gap: 4px; }
.sz-fs-preset {
  display: inline-flex; align-items: center; justify-content: center;
  height: 24px; padding: 0 8px;
  background: #FFFFFF;
  border: 1px solid #E5E5E5;
  border-radius: 999px;
  cursor: pointer; user-select: none;
  font-size: 12px; font-weight: 500; color: #000000;
}
.sz-fs-preset:hover { background: rgba(56,142,60,0.04); }
.sz-fs-preset--on {
  background: #DFEFE3;
  border-color: transparent;
}

.sz-fs-date-row {
  display: flex;
  gap: 8px;
}
.sz-fs-date-field {
  flex: 1; min-width: 0;
  display: flex; flex-direction: column; gap: 4px;
}
.sz-fs-date-prefix {
  font-size: 12px; font-weight: 500; color: #6B7280;
}

/* Footer */
.sz-fs-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 4px;
}
.sz-fs-btn {
  height: 36px;
  padding: 0 16px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px; font-weight: 500;
  cursor: pointer;
  border: none;
}
.sz-fs-btn--ghost {
  background: #FFFFFF;
  border: 1px solid #D4D4D4;
  color: #000000;
}
.sz-fs-btn--ghost:hover { background: rgba(0,0,0,0.04); }
.sz-fs-btn--primary {
  background: #388E3C;
  color: #FFFFFF;
}
.sz-fs-btn--primary:hover { background: #2F7A33; }

/* Transitions */
.sz-fs-enter-active, .sz-fs-leave-active {
  transition: opacity 0.2s ease-out, transform 0.2s ease-out;
}
.sz-fs-enter-from, .sz-fs-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* ── Dark mode ── */
.dark .sz-fs { background: #1F2125; }
.dark .sz-fs-header { background: #1F2125; border-bottom-color: #2A2C30; }
.dark .sz-fs-close, .dark .sz-fs-title, .dark .sz-fs-section-label { color: #E5E7EB; }
.dark .sz-fs-funnel { background: #2A2C30; color: #E5E7EB; }
.dark .sz-fs-segmented { background: #242528; }
.dark .sz-fs-segmented-item span { color: #9797A0; }
.dark .sz-fs-segmented-item--on { background: #3D3F43; }
.dark .sz-fs-segmented-item--on span { color: #FFFFFF; }
.dark .sz-fs-prio {
  background: transparent; border-color: #3D3F43;
}
.dark .sz-fs-prio-label { color: #E5E7EB; }
.dark .sz-fs-prio--on { background: rgba(56,142,60,0.22); border-color: transparent; }
.dark .sz-fs-multi-field { background: #242528; border-color: #303138; }
.dark .sz-fs-placeholder { color: #92939A; }
.dark .sz-fs-multi-chevron { color: #9CA3AF; }
.dark .sz-fs-tag-text { color: #BBBBBE; }
.dark .sz-fs-tag-agent { background: rgba(255,255,255,0.08); }
/* X — caixinha cinza escura no dark (Sadraque #237) */
.dark .sz-fs-tag-x { background: #333333; color: #9C9C9C; }
.dark .sz-fs-tag-x:hover { background: #404040; color: #E5E5E5; }
.dark .sz-fs-multi-dropdown { background: #151518; border-color: #303134; box-shadow: 0 4px 16px rgba(0,0,0,0.5); }
.dark .sz-fs-multi-option:hover { background: rgba(138,206,138,0.10); }
.dark .sz-fs-multi-option--on { background: rgba(138,206,138,0.18); }
.dark .sz-fs-option-text { color: #9797A0; }
.dark .sz-fs-option-text--bold { color: #E5E7EB; }
/* Search dentro do dropdown — dark */
/* Dark mode: wrapper is transparent, only the bottom divider needs to be
   darker. Input/text colors flip. */
.dark .sz-fs-dd-search {
  background: #1B1B1F !important;
  border-color: #303138 !important;
}
.dark .sz-fs-dd-search-icon { color: rgba(255,255,255,0.4) !important; }
.dark .sz-fs-dd-search-input { color: #E5E7EB !important; }
.dark .sz-fs-dd-search-input::placeholder { color: rgba(255,255,255,0.4) !important; opacity: 1 !important; }
/* Checkbox — dark (Sadraque #237) */
.dark .sz-fs-checkbox { background: #171717; border-color: #525252; }
.dark .sz-fs-checkbox--on { background: #59AA59; border-color: #59AA59; color: #FFFFFF; }
/* Gradient bottom — dark */
.dark .sz-fs-dd-gradient {
  background: linear-gradient(180deg, rgba(21,21,24,0) 0%, #151518 100%);
}
.dark .sz-fs-channel-icon { color: #9797A0; }
.dark .sz-fs-preset { background: transparent; border-color: #3D3F43; color: #E5E7EB; }
.dark .sz-fs-preset--on { background: rgba(56,142,60,0.22); border-color: transparent; color: #FFFFFF; }
.dark .sz-fs-date-prefix { color: #9CA3AF; }
.dark .sz-fs-btn--ghost { background: transparent; border-color: #3E3E43; color: #E5E7EB; }
.dark .sz-fs-btn--ghost:hover { background: rgba(255,255,255,0.05); }
</style>
